import React, {forwardRef, useRef} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Form from "../../../../base/form";
import {Col} from "reactstrap";
import {OutlinedInputForm} from "../../../../base/input/outlined_input";
import {OutlinedDropdownForm} from "../../../../base/dropdown/outlined_dropdown";
import {createUUId} from "../../../../../../core/services/utils/utils";


export const ExtraFeesValueTypes = {
    price: {id: 1, title: '$'},
    percent: {id: 2, title: '%'},
}

const schema = Yup.object().shape({
    minDays: Yup.number().when('maxDays', {
        is: val => !val,
        then: Yup.number()
            .min(0, ValidateMessages.greaterThan('0'))
            .nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType("number")),
        otherwise: Yup.number()
            .min(0, ValidateMessages.greaterThan('0'))
            // .lessThan(Yup.ref('maxDays'), ValidateMessages.lessThan('max days'))
            .nullable().required(ValidateMessages?.required).typeError(ValidateMessages.incorrectType("number"))
    }),
    maxDays: Yup.number().when('minDays', {
        is: val => !val,
        then: Yup.number()
            .min(1, ValidateMessages.greaterThan('1'))
            .nullable().typeError(ValidateMessages.incorrectType("number")),
        otherwise: Yup.number()
            .min(1, ValidateMessages.greaterThan('1'))
            // .moreThan(Yup.ref('minDays'), ValidateMessages.greaterThan('min days'))
            .nullable().typeError(ValidateMessages.incorrectType("number"))
    }),
    title: Yup.object().nullable().required(ValidateMessages.required),
    type: Yup.object().nullable().required(ValidateMessages.required),
    fee: Yup.number().min(0).typeError(ValidateMessages.incorrectType("number")).nullable().required(ValidateMessages.required)
}, ['minDays', 'maxDays']);


const valueTypes = [ExtraFeesValueTypes.price, ExtraFeesValueTypes.percent];


export const WarrantyAndReturnExtraFeesForm = forwardRef(({
                                                              onSubmit,
                                                              titles,
                                                              openTitleManagement,
                                                              children,
                                                              initialValues = {}
                                                          }, ref) => {
    const validate = makeValidate(schema)
    const required = makeRequired(schema)
    const id = useRef(`_${createUUId()}`)
    const minDaysId = `min-days-input-${id.current}`;
    const maxDaysId = `max-days-input-${id.current}`;

    /**
     * Checks if the selected option is specific for title management, and if so, removes the selected option and
     * invokes openTitleManagement callback.
     *
     * @param {MouseEvent} e
     * @param {formApi} form
     */
    const onTitleChanged = (e, form) => {
        if (e.target.value === null) {
            form.resetFieldState('title')
            openTitleManagement()
        }
    }

    return (
        <>
            <Form
                className='form-section'
                onSubmit={onSubmit}
                validate={validate}
                initialValues={initialValues}
                ref={ref}
                render={({values, initialValues, form, hasValidationErrors}) => {
                    return (
                        <>
                            <Col xs={3} lg={2} className={'position-relative'}>
                                <OutlinedInputForm
                                    name={'minDays'}
                                    label={"Min Days"}
                                    placeholder={'min days'}
                                    type={'number'}
                                    required={required.minDays}
                                    value={values.minDays ?? ''}
                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                    inputProps={{
                                        min: 0,
                                        max: values.maxDays,
                                        className: minDaysId,
                                    }}
                                />
                                <div className={'pt-3'}/>
                                <OutlinedInputForm
                                    name={'maxDays'}
                                    label={"Max Days"}
                                    placeholder={'max days'}
                                    className={'mb-3'}
                                    type={'number'}
                                    required={required.maxDays}
                                    value={values.maxDays ?? ''}
                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                    inputProps={{
                                        min: Math.max(values.minDays ?? 1, 1),
                                        className: maxDaysId,
                                    }}
                                />
                            </Col>
                            <Col xs={8} className={'d-flex flex-wrap'}>
                                <Col xs={12} className={'mb-3'}>
                                    <OutlinedDropdownForm
                                        label={'Title'}
                                        name={'title'}
                                        placeholder={'Title'}
                                        required={required.title}
                                        onChange={(e) => onTitleChanged(e, form)}
                                        data={[
                                            {
                                                value: null,
                                                label: 'Add and Manage Titles',
                                                disabled: false,
                                                className: 'extra-fees-manage-titles',
                                                id: -1,
                                            },
                                            ...(titles?.map(e => ({
                                                value: e,
                                                label: e.title,
                                                id: e.id,
                                            })) ?? [])
                                        ]}
                                    />
                                </Col>
                                <Col xs={4} className={'mb-3'}>
                                    <OutlinedDropdownForm
                                        label={'Type'}
                                        name={'type'}
                                        placeholder={''}
                                        required={required.type}
                                        data={valueTypes?.map(e => ({
                                            value: e,
                                            label: e.title,
                                            id: e.id,
                                        })) ?? []}
                                    />
                                </Col>
                                <Col xs={8} className={'mb-3'}>
                                    <OutlinedInputForm
                                        name={'fee'}
                                        label={"Fee"}
                                        type={'number'}
                                        placeholder={'e.g. 100'}
                                        required={required.fee}
                                        value={values.fee ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                        inputProps={{
                                            min: 0,
                                        }}
                                    />
                                </Col>
                            </Col>
                            {
                                typeof children === 'function'
                                    ? children(form, values, initialValues, hasValidationErrors)
                                    : children
                            }

                        </>
                    )
                }}
            />
        </>
    )
})

export default WarrantyAndReturnExtraFeesForm;
